/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { WithRouter } from '@/components/WithRouter'

import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import { Menu, Dropdown, message, Drawer, Button } from 'antd'
import { EllipsisOutlined, MenuOutlined } from '@ant-design/icons'
import menusConfig, {
  MATERNITY_GROUP_KEY,
  TUINA_GROUP_KEY,
  getClientTopGroupKey,
  getMaternityDefaultPath,
  getTuinaDefaultPath
} from './menus'
import { debounce } from 'lodash'
import { fetchPermissionCodesByType } from '@/redux/app/appSlice'
import { mergeClientPermissionCodeLists } from '@/constants/menuPermissionMeta'
import { isMenuItemVisible, stripMenuRBACFields } from '@/utils/menuItemVisible'
import { alignHostErpToken } from '@/utils/auth'
import './index.less'
import DropdownMenu from './DropdownMenu'

const AppHeader = ({ location, navigate }) => {
  const dispatch = useDispatch()
  const { user, token, browserType, permissionCodes, permissionCodesLoaded } = useSelector(state => state.app)
  const mergedRbacCodes = useMemo(
    () => mergeClientPermissionCodeLists(permissionCodes),
    [permissionCodes]
  )
  const [expandable, setExpandable] = useState(false) // 隐藏菜单开关
  const [menuSplit, setMenuSplit] = useState(0)
  const [menuData, setMenuData] = useState([])
  /** 完整菜单（不受横向宽度裁剪），供手机端抽屉使用 */
  const [fullMenuItems, setFullMenuItems] = useState([])
  const [dropdownData, setDropdownData] = useState([])
  const [hasComputed, setHasComputed] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [isMobileNav, setIsMobileNav] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 768px)').matches
  })
  const rootRef = useRef(null)
  const prevMobileNavRef = useRef(false)

  // §4.3 / 阶段 E：顶部菜单切换后静默拉权限码；防抖避免连点并发乱序
  const debouncedSilentRefreshPermissionCodes = useRef(
    debounce((dispatchFn) => {
      dispatchFn(fetchPermissionCodesByType())
        .unwrap()
        .catch((err) => {
          console.warn('[AppHeader] 静默刷新权限码失败', err)
        })
    }, 400)
  )

  useEffect(() => {
    const d = debouncedSilentRefreshPermissionCodes.current
    return () => {
      d.cancel()
    }
  }, [])

  // 窄屏：抽屉导航；监听横竖屏与窗口变化
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = () => setIsMobileNav(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // 从手机切回桌面：恢复完整横向菜单并重新计算宽度
  useEffect(() => {
    if (prevMobileNavRef.current && !isMobileNav) {
      setMenuData(fullMenuItems)
      setDropdownData([])
      setExpandable(false)
      setHasComputed(false)
    }
    prevMobileNavRef.current = isMobileNav
  }, [isMobileNav, fullMenuItems])

  // §4.5：与路由守卫一致，菜单/模块/按钮码并集与配置项求交
  const checkMenuVisible = useCallback((menu) => {
    return isMenuItemVisible(menu, mergedRbacCodes, permissionCodesLoaded)
  }, [mergedRbacCodes, permissionCodesLoaded])

  // 递归子菜单；无可见子项时隐藏父级（§4.5.1）
  const renderMenu = useCallback((menu) => {
    if (!menu) return null

    if (menu.children && Array.isArray(menu.children)) {
      const filterMenus = menu.children
        .filter(childMenu => checkMenuVisible(childMenu))
        .map(childMenu => renderMenu(childMenu))
        .filter(Boolean)

      if (filterMenus.length === 0) {
        return null
      }
      return {
        ...menu,
        children: filterMenus
      }
    }
    return menu
  }, [checkMenuVisible])

  // 初始化菜单数据
  useEffect(() => {
    try {
      const filterMenus = menusConfig
        .filter(menu => checkMenuVisible(menu))
        .map(menu => renderMenu(menu))
        .filter(Boolean)
      const withCodesStripped = filterMenus.map(stripMenuRBACFields).filter(Boolean)
      const headerItems = withCodesStripped.map((item) => {
        if (item.key === MATERNITY_GROUP_KEY || item.key === TUINA_GROUP_KEY) {
          const { children, ...rest } = item
          return rest
        }
        return item
      })
      setFullMenuItems(withCodesStripped)
      setMenuData(headerItems)
    } catch (err) {
      console.error('AppHeader 初始化菜单失败:', err)
      // 如果出错，至少显示原始菜单配置
      const fallback = menusConfig.map(stripMenuRBACFields).filter(Boolean)
      setFullMenuItems(fallback)
      setMenuData(menusConfig)
    }
  }, [checkMenuVisible, renderMenu])

  // 计算菜单宽度并决定是否需要折叠（手机端用抽屉展示全量菜单，不做横向裁剪）
  useEffect(() => {
    if (isMobileNav) {
      if (!hasComputed) {
        setHasComputed(true)
      }
      return
    }
    // 确保DOM已经渲染完成
    if (hasComputed || !rootRef.current || !document || !document.querySelector) {
      return
    }

    // 等待菜单渲染完成 - 使用setTimeout确保DOM已更新
    const timer = setTimeout(() => {
      try {
        const checkableTagElements = rootRef.current?.querySelectorAll?.('.ant-menu-submenu, .ant-menu-item')
        
        if (!checkableTagElements || checkableTagElements.length === 0) {
          // 如果菜单还没渲染，稍后重试
          return
        }

        const bodyElement = document.querySelector('body')
        if (!bodyElement) {
          console.warn('AppHeader: 无法找到body元素')
          setHasComputed(true)
          return
        }

        const menuWidth = (bodyElement.offsetWidth || window.innerWidth || 1200) - 338
        if (menuWidth <= 0) {
          console.warn('AppHeader: 计算出的菜单宽度无效')
          setHasComputed(true)
          return
        }

        // 计算每个菜单项的宽度
        const menuItemWidths = Array.from(checkableTagElements).map(element => {
          try {
            const rect = element.getBoundingClientRect?.()
            if (!rect) return 0
            
            const width = rect.width || 0
            const computedStyle = window.getComputedStyle?.(element)
            const marginLeft = computedStyle ? (parseInt(computedStyle.marginLeft, 10) || 0) : 0
            const marginRight = computedStyle ? (parseInt(computedStyle.marginRight, 10) || 0) : 0

            return width + marginLeft + marginRight
          } catch (e) {
            console.warn('AppHeader: 计算菜单项宽度失败', e)
            return 0
          }
        })

        // 计算总宽度和找到分割点
        let totalWidth = 0
        let currentMenuSplit = 0

        for (let i = 0; i < menuItemWidths.length; i++) {
          totalWidth += menuItemWidths[i]
          if (totalWidth < menuWidth) {
            currentMenuSplit = i + 1
          } else {
            break
          }
        }

        setHasComputed(true)

        // 如果总宽度超过可用宽度，需要折叠
        if (totalWidth > menuWidth && currentMenuSplit < menuData.length) {
          setExpandable(true)
          setMenuSplit(currentMenuSplit)
          setMenuData(prevMenuData => prevMenuData.slice(0, currentMenuSplit))
          setDropdownData(prevMenuData => prevMenuData.slice(currentMenuSplit))
        }
      } catch (err) {
        console.error('AppHeader 计算菜单宽度失败:', err)
        setHasComputed(true) // 即使出错也标记为已计算，避免无限重试
      }
    }, 100) // 延迟100ms确保DOM已渲染

    return () => clearTimeout(timer)
  }, [menuData, hasComputed, isMobileNav])

  useEffect(() => {
    // 只在当前主机的 ik 与本机 ERP 票不一致时刷新（另一环境的父域 Cookie 已不会进来）
    if (alignHostErpToken() === 'adopted') {
      navigate('/client')
      message.success('系统正在刷新，请稍后～！', 5)
    }
  }, [token, navigate])



  // 具体子菜单点击控制（navigate 后与静默刷新并行；无成功 Toast）
  const handleMenuClick = useCallback(
    (info) => {
      const key = info?.key
      const { pathname: nowPath } = location
      if (key === MATERNITY_GROUP_KEY) {
        if (getClientTopGroupKey(nowPath) !== MATERNITY_GROUP_KEY) {
          navigate(getMaternityDefaultPath(fullMenuItems))
        }
      } else if (key === TUINA_GROUP_KEY) {
        if (!nowPath.startsWith('/client/tuina') && !nowPath.startsWith('/client/massage')) {
          navigate(getTuinaDefaultPath(fullMenuItems))
        }
      } else if (typeof key === 'string' && key.startsWith('/')) {
        navigate(key)
      }
      debouncedSilentRefreshPermissionCodes.current(dispatch)
      if (isMobileNav) {
        setMobileDrawerOpen(false)
      }
    },
    [navigate, dispatch, isMobileNav, location, fullMenuItems]
  )


  const { pathname } = location
  const topGroupKey = getClientTopGroupKey(pathname)
  const headerSelectedKeys = topGroupKey === 'system' || !topGroupKey
    ? [pathname]
    : [topGroupKey]
  const isWorldHud = pathname.includes('/worldCommandCenter')
  const cls = classNames('i2-app-header', {
    ['hasExpandTag']: expandable && !isMobileNav,
    'i2-app-header--mobile': isMobileNav,
    'i2-app-header--world-hud': isWorldHud,
  })
  const menuDropdownProps = {
    items: dropdownData,
    onClick: handleMenuClick,
    selectable: true,
    selectedKeys: headerSelectedKeys
  }

  return (
    <header
      ref={rootRef}
      className={classNames({ 'i2-app-header-outer--world-hud': isWorldHud })}
      style={{
        borderBottom: isWorldHud ? '1px solid rgba(0, 212, 170, 0.2)' : '1px solid #e8e8e8',
      }}
    >
      <div className={cls}>
        <div className='logo'/>

        {isMobileNav ? (
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 18 }} />}
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="打开导航菜单"
            className="i2-app-header-mobile-trigger"
          />
        ) : null}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }} className="i2-app-header-menu-row">
          {!isMobileNav ? (
            <>
              <Menu
                style={{ borderBottom: 0, flex: 1, minWidth: 0 }}
                mode="horizontal"
                selectedKeys={headerSelectedKeys}
                defaultSelectedKeys={headerSelectedKeys}
                onClick={handleMenuClick}
                items={menuData}
                overflowedIndicator={
                  <EllipsisOutlined style={{ fontSize: 16 }} />
                }
              />
              {
                expandable && dropdownData.length > 0
                  ? <Dropdown
                    menu={menuDropdownProps}
                    placement="bottomRight"
                    getPopupContainer={(triggerNode) => {
                      // 确保下拉菜单挂载在正确的位置
                      const header = triggerNode?.closest?.('header') || triggerNode?.parentElement
                      return header || document.body
                    }}
                    overlayStyle={{
                      maxHeight: 'calc(100vh - 100px)',
                      overflowY: 'auto',
                      zIndex: 1050
                    }}
                    trigger={['click', 'hover']}
                  >
                    <div 
                      className="header-more-menu"
                      style={{ 
                        width: 50, 
                        height: 46, 
                        lineHeight: '46px', 
                        textAlign: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <EllipsisOutlined style={{ fontSize: 16 }} />
                    </div>
                  </Dropdown>
                  : null
              }
            </>
          ) : null}
        </div>
        <aside className="right">
          <DropdownMenu
            navigate={navigate}
          />
        </aside>
      </div>

      <Drawer
        title="导航菜单"
        placement="left"
        width={Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 24 : 320)}
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        destroyOnClose={false}
        styles={{ body: { padding: 0 } }}
        className="i2-app-header-mobile-drawer"
      >
        <Menu
          mode="inline"
          selectedKeys={[pathname]}
          onClick={handleMenuClick}
          items={fullMenuItems}
          style={{ borderRight: 0 }}
        />
      </Drawer>
    </header>
  )
}

export default WithRouter(AppHeader)
